// Built-in Excel formats (ids 4, 2 and 1): locale-neutral codes the viewer renders with its own
// thousands and decimal separators.
export const AMOUNT_FORMAT = '#,##0.00';
const QUANTITY_FORMAT = '0.00';
const INTEGER_FORMAT = '0';

/**
 * A numeric export cell: `generateStyledXLSX` writes `value` as a number cell carrying `numFmt`,
 * and a `null` value as a blank cell rather than an empty text cell. `width` is the cell's length in
 * characters as its format shows it, for a format that shows more than a plain amount, a currency or
 * a unit beside it; without one the column keeps the default number width.
 */
export class ExcelNumber {
    constructor(
        readonly value: number | null,
        readonly numFmt: string,
        readonly width?: number
    ) {}
}

type NumericInput = string | number | null | undefined;

const toNumber = (input: NumericInput): number | null => {
    if (input === null || input === undefined || (typeof input === 'string' && input.trim() === '')) {
        return null;
    }
    const value = Number(input);
    return Number.isFinite(value) ? value : null;
};

/**
 * Money at full precision: `#,##0.00` rounds it for display only, so a SUM over the column matches the
 * source total. Accepts the string amounts some export DTOs carry.
 */
export const toExcelAmount = (input: NumericInput): ExcelNumber => new ExcelNumber(toNumber(input), AMOUNT_FORMAT);

/** Hours, multipliers and ratings, shown with 2 decimals. */
export const toExcelQuantity = (input: NumericInput): ExcelNumber => new ExcelNumber(toNumber(input), QUANTITY_FORMAT);

export const toExcelInteger = (input: NumericInput): ExcelNumber => new ExcelNumber(toNumber(input), INTEGER_FORMAT);

/**
 * A `yyyy-MM-dd` date as a UTC-midnight `Date`. ExcelJS converts a `Date` by its UTC timestamp, so a
 * local-midnight parse (`parseISO`, date-fns `parse`) would land on a fractional serial east or west
 * of UTC. Written with no numFmt, ExcelJS gives it the built-in short-date format (id 14), which
 * Excel renders in the viewer's regional date format.
 */
export const toExcelDate = (isoDate: string | null | undefined): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
    return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))) : null;
};
